import {
  instrumentCtrlValue,
  isTrackerControlNote,
  isTrackerPitchNote,
  isValidEffectCode,
  TRACKER_CHANNEL_COUNT,
  TRACKER_EFFECT,
  TRACKER_INSTRUMENT_EMPTY,
  TRACKER_MAX_COMPILED_INSTRUMENTS,
  TRACKER_MAX_COMPILED_PATTERNS,
  TRACKER_MAX_ORDERS,
  TRACKER_NOTE_EMPTY,
  TRACKER_ROWS_PER_PATTERN,
  type ChannelEvent,
  type TrackerInstrument,
  type TrackerProject
} from "./model.js";
import { TRACKER_BYTECODE_MAX_SIZE } from "./emulator-status.js";
import { isNoteValidForPitchMode, noteCodeToCompactLabel, pitchModeDisplayName, pitchModeRangeLabel } from "./note-data.js";

const BYTECODE_MAGIC = [0x41, 0x4d, 0x38, 0x54];
const BYTECODE_VERSION = 1;
const HEADER_SIZE = 28;
const INSTRUMENT_SIZE = 8;
const EVENT_SIZE = 4;
const PATTERN_SIZE = TRACKER_ROWS_PER_PATTERN * TRACKER_CHANNEL_COUNT * EVENT_SIZE;

const WARN_RATIO = 0.75;

export type CompiledTrackerSong = {
  bytecode: Uint8Array;
  compiledOrderList: number[];
  instrumentMap: number[];
  originalOrderList: number[];
  patternMap: number[];
  tickReload: number;
};

export type TrackerCompileMetricSeverity = "ok" | "warn" | "error";

export type TrackerCompileMetric = {
  label: string;
  max: number;
  severity: TrackerCompileMetricSeverity;
  used: number;
};

export type TrackerPitchRangeError = {
  channel: number;
  message: string;
  note: number;
  pattern: number;
  row: number;
};

export type TrackerCompileAnalysis = {
  compiledOrderList: number[];
  instrumentOffset: number;
  instrumentMap: number[];
  metrics: TrackerCompileMetric[];
  noteErrors: TrackerPitchRangeError[];
  originalOrderList: number[];
  orderOffset: number;
  patternMap: number[];
  patternOffset: number;
  status: TrackerCompileMetricSeverity;
  tickReload: number;
  totalSize: number;
};

function writeWord(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >> 8) & 0xff;
}

function encodeInstrument(instrument: TrackerInstrument): Uint8Array {
  const bytes = new Uint8Array(INSTRUMENT_SIZE);
  bytes[0] = instrumentCtrlValue(instrument);
  bytes[1] = instrument.ad & 0xff;
  bytes[2] = instrument.sr & 0xff;
  bytes[3] = instrument.pulseWidth & 0xff;
  bytes[4] = (instrument.pulseWidth >> 8) & 0x0f;
  bytes[5] = instrument.auxRateX8 ? 0x01 : 0x00;
  bytes[6] = instrument.filterRouteEnabled ? 0x01 : 0x00;
  bytes[7] = 0x00;
  return bytes;
}

function encodeEvent(event: ChannelEvent, instrumentMap: Map<number, number>): Uint8Array {
  const bytes = new Uint8Array(EVENT_SIZE);
  if (
    event.note !== TRACKER_NOTE_EMPTY
    && !isTrackerControlNote(event.note)
    && !isTrackerPitchNote(event.note)
  ) {
    throw new Error(`tracker note ${event.note} is outside the supported C-2..B-7/OFF/CUT range`);
  }
  if (event.effectArg < 0 || event.effectArg > 0xff) {
    throw new Error(`tracker effect argument ${event.effectArg} exceeds the v1 8-bit range`);
  }
  if (!isValidEffectCode(event.effect)) {
    throw new Error(`tracker effect opcode ${event.effect} is unknown`);
  }
  bytes[0] = event.note & 0xff;
  if (event.instrument === TRACKER_INSTRUMENT_EMPTY) {
    bytes[1] = TRACKER_INSTRUMENT_EMPTY;
  } else {
    const mapped = instrumentMap.get(event.instrument);
    if (mapped === undefined) {
      throw new Error(`tracker instrument ${event.instrument + 1} is not available in the compiled song`);
    }
    bytes[1] = mapped & 0xff;
  }
  bytes[2] = event.effect & 0xff;
  bytes[3] = event.effectArg & 0xff;
  return bytes;
}

export function computeTickReload(tempo: number): number {
  const sanitizedTempo = Math.max(32, Math.min(255, Math.round(tempo)));
  return Math.max(1, Math.min(0xffff, Math.round(120000 / sanitizedTempo)));
}

type CompileOptions = {
  orderList?: number[];
};

function metricSeverity(used: number, max: number): TrackerCompileMetricSeverity {
  if (used > max) {
    return "error";
  }
  if (max > 0 && used / max >= WARN_RATIO) {
    return "warn";
  }
  return "ok";
}

function mergeSeverity(current: TrackerCompileMetricSeverity, next: TrackerCompileMetricSeverity): TrackerCompileMetricSeverity {
  if (current === "error" || next === "error") {
    return "error";
  }
  if (current === "warn" || next === "warn") {
    return "warn";
  }
  return "ok";
}

function collectPitchModeNoteErrors(project: TrackerProject): TrackerPitchRangeError[] {
  const errors: TrackerPitchRangeError[] = [];
  const pitchMode = project.song.globals.pitchMode;
  const range = pitchModeRangeLabel(pitchMode);
  const modeName = pitchModeDisplayName(pitchMode);
  for (let pattern = 0; pattern < project.song.patterns.length; pattern += 1) {
    const currentPattern = project.song.patterns[pattern];
    for (let row = 0; row < currentPattern.rows.length; row += 1) {
      const currentRow = currentPattern.rows[row];
      for (let channel = 0; channel < currentRow.length; channel += 1) {
        const event = currentRow[channel];
        if (isNoteValidForPitchMode(event.note, pitchMode)) {
          continue;
        }
        errors.push({
          channel,
          message: `${modeName} supports only ${range}; found ${noteCodeToCompactLabel(event.note)} at Pattern ${pattern
            .toString()
            .padStart(2, "0")} Row ${row.toString().padStart(2, "0")} V${channel + 1}`,
          note: event.note,
          pattern,
          row
        });
      }
    }
  }
  return errors;
}

function noteErrorSummary(noteErrors: TrackerPitchRangeError[]): string {
  if (noteErrors.length === 0) {
    return "";
  }
  if (noteErrors.length === 1) {
    return noteErrors[0].message;
  }
  return `${noteErrors[0].message} (+${noteErrors.length - 1} more)`;
}

export function analyzeTrackerProject(project: TrackerProject, options: CompileOptions = {}): TrackerCompileAnalysis {
  const orderList = (options.orderList ?? project.song.orderList).slice(0, TRACKER_MAX_ORDERS);
  if (orderList.length === 0) {
    throw new Error("tracker order list must contain at least one pattern");
  }

  const patternMap: number[] = [];
  const patternMapLookup = new Map<number, number>();
  for (const patternIndex of orderList) {
    if (!Number.isInteger(patternIndex) || patternIndex < 0 || patternIndex >= project.song.patterns.length) {
      throw new Error(`tracker order references missing pattern ${patternIndex}`);
    }
    if (!patternMapLookup.has(patternIndex)) {
      patternMapLookup.set(patternIndex, patternMap.length);
      patternMap.push(patternIndex);
    }
  }
  const instrumentMap: number[] = [];
  const instrumentLookup = new Map<string, number>();
  for (const patternIndex of patternMap) {
    const pattern = project.song.patterns[patternIndex];
    for (const row of pattern.rows) {
      for (const event of row) {
        if (event.instrument === TRACKER_INSTRUMENT_EMPTY) {
          continue;
        }
        const instrument = project.song.instruments[event.instrument];
        if (instrument === undefined) {
          throw new Error(`tracker pattern references missing instrument ${event.instrument + 1}`);
        }
        const fingerprint = JSON.stringify(encodeInstrument(instrument));
        if (!instrumentLookup.has(fingerprint)) {
          instrumentLookup.set(fingerprint, instrumentMap.length);
          instrumentMap.push(event.instrument);
        }
      }
    }
  }
  const compiledOrderList = orderList.map((patternIndex) => patternMapLookup.get(patternIndex) ?? 0);
  const instrumentOffset = HEADER_SIZE;
  const orderOffset = instrumentOffset + instrumentMap.length * INSTRUMENT_SIZE;
  const patternOffset = orderOffset + compiledOrderList.length;
  const totalSize = patternOffset + patternMap.length * PATTERN_SIZE;

  const metrics: TrackerCompileMetric[] = [
    {
      label: "Patterns",
      max: TRACKER_MAX_COMPILED_PATTERNS,
      severity: metricSeverity(patternMap.length, TRACKER_MAX_COMPILED_PATTERNS),
      used: patternMap.length
    },
    {
      label: "Instruments",
      max: TRACKER_MAX_COMPILED_INSTRUMENTS,
      severity: metricSeverity(instrumentMap.length, TRACKER_MAX_COMPILED_INSTRUMENTS),
      used: instrumentMap.length
    },
    {
      label: "Orders",
      max: TRACKER_MAX_ORDERS,
      severity: metricSeverity(compiledOrderList.length, TRACKER_MAX_ORDERS),
      used: compiledOrderList.length
    },
    {
      label: "Bytes",
      max: TRACKER_BYTECODE_MAX_SIZE,
      severity: metricSeverity(totalSize, TRACKER_BYTECODE_MAX_SIZE),
      used: totalSize
    }
  ];
  let status: TrackerCompileMetricSeverity = "ok";
  for (const metric of metrics) {
    status = mergeSeverity(status, metric.severity);
  }
  const noteErrors = collectPitchModeNoteErrors(project);
  if (noteErrors.length > 0) {
    status = "error";
  }

  return {
    compiledOrderList,
    instrumentOffset,
    instrumentMap,
    metrics,
    noteErrors,
    originalOrderList: orderList,
    orderOffset,
    patternMap,
    patternOffset,
    status,
    tickReload: computeTickReload(project.song.tempo),
    totalSize
  };
}

export function compileTrackerProject(project: TrackerProject, options: CompileOptions = {}): CompiledTrackerSong {
  const analysis = analyzeTrackerProject(project, options);
  const {
    compiledOrderList,
    instrumentMap,
    instrumentOffset,
    noteErrors,
    originalOrderList,
    orderOffset,
    patternMap,
    patternOffset,
    tickReload,
    totalSize
  } = analysis;
  if (noteErrors.length > 0) {
    throw new Error(noteErrorSummary(noteErrors));
  }
  if (patternMap.length > TRACKER_MAX_COMPILED_PATTERNS) {
    throw new Error(`tracker compile uses ${patternMap.length} patterns but v1 supports at most ${TRACKER_MAX_COMPILED_PATTERNS}`);
  }
  if (instrumentMap.length > TRACKER_MAX_COMPILED_INSTRUMENTS) {
    throw new Error(`tracker compile uses ${instrumentMap.length} instruments but v1 supports at most ${TRACKER_MAX_COMPILED_INSTRUMENTS}`);
  }
  if (totalSize > TRACKER_BYTECODE_MAX_SIZE) {
    throw new Error(`tracker bytecode is ${totalSize} bytes, exceeding the ${TRACKER_BYTECODE_MAX_SIZE}-byte workspace`);
  }
  const bytes = new Uint8Array(totalSize);
  bytes.set(BYTECODE_MAGIC, 0);
  bytes[4] = BYTECODE_VERSION;
  bytes[5] = TRACKER_ROWS_PER_PATTERN;
  bytes[6] = TRACKER_CHANNEL_COUNT;
  bytes[7] = 0x00;
  bytes[8] = project.song.speed & 0xff;
  bytes[9] = project.song.tempo & 0xff;
  writeWord(bytes, 10, tickReload);
  bytes[12] = project.song.globals.pitchMode & 0xff;
  bytes[13] = project.song.globals.masterVolume & 0x0f;
  writeWord(bytes, 14, project.song.globals.filterCutoff & 0x07ff);
  bytes[16] = project.song.globals.filterCtrl & 0xff;
  bytes[17] = instrumentMap.length & 0xff;
  bytes[18] = patternMap.length & 0xff;
  bytes[19] = compiledOrderList.length & 0xff;
  writeWord(bytes, 20, instrumentOffset);
  writeWord(bytes, 22, orderOffset);
  writeWord(bytes, 24, patternOffset);
  writeWord(bytes, 26, totalSize);

  instrumentMap.forEach((projectIndex, index) => {
    bytes.set(encodeInstrument(project.song.instruments[projectIndex]), instrumentOffset + index * INSTRUMENT_SIZE);
  });
  bytes.set(compiledOrderList, orderOffset);

  const compiledInstrumentLookup = new Map<number, number>();
  instrumentMap.forEach((projectIndex, compiledIndex) => {
    compiledInstrumentLookup.set(projectIndex, compiledIndex);
  });

  patternMap.forEach((patternIndex, compiledIndex) => {
    const pattern = project.song.patterns[patternIndex];
    let writeOffset = patternOffset + compiledIndex * PATTERN_SIZE;
    for (const row of pattern.rows) {
      for (const event of row) {
        bytes.set(encodeEvent(event, compiledInstrumentLookup), writeOffset);
        writeOffset += EVENT_SIZE;
      }
    }
  });

  return {
    bytecode: bytes,
    compiledOrderList,
    instrumentMap,
    originalOrderList,
    patternMap,
    tickReload
  };
}

export function compileTrackerPreview(project: TrackerProject, activePattern: number): CompiledTrackerSong {
  return compileTrackerProject(project, { orderList: [activePattern] });
}

export function trackHasEffect(event: ChannelEvent): boolean {
  return event.effect !== TRACKER_EFFECT.none && event.effect !== TRACKER_EFFECT.off;
}
